import { useMemo } from "react";
import { useAdvancedCurrencyDisplay } from "@/hooks/useAdvancedCurrencyDisplay";
import { useUserSettings } from "@/hooks/useUserSettings";
import { getDisplayAmount } from "@/lib/currency";
import {
  getStatusVariant,
  getReconciliationIcon as getReconciliationIconLib,
  getFrequencyText,
  getCategoryIcon,
} from "@/lib/subscriptionDisplay";
import { type Subscription } from "@/types/subscription";

export const useSubscriptionDisplay = (subscription: Subscription) => {
  const { userSettings } = useUserSettings();
  // ADR-008: prefer advanced currency display (current rate for subscriptions)
  const adv = useAdvancedCurrencyDisplay(
    subscription.amount,
    subscription.currency,
    undefined, // no transaction date for base subscription amount
    { displayCurrency: userSettings?.currency || "EUR" },
  );

  return useMemo(() => {
    const fallback = getDisplayAmount(
      subscription.amount,
      subscription.currency,
      userSettings?.currency || "EUR",
    ).formatted;

    const formatAmount = () => {
      return adv.data?.formattedAmount ?? fallback;
    };

    return {
      formatAmount,
      getFrequencyText: () => getFrequencyText(subscription),
      getStatusVariant: () => getStatusVariant(subscription.status),
      getReconciliationIcon: () => {
        const icon = getReconciliationIconLib(
          subscription.reconciliationStatus,
        );
        if (icon === "CheckCircle") return "Check";
        if (icon === "Clock") return "AlertCircle";
        return icon;
      },
      getCategoryIcon: () => getCategoryIcon(subscription.category),
    };
  }, [subscription, userSettings?.currency, adv.data?.formattedAmount]);
};
